import { CatalogView, SettingsView } from '@lemoncloud/codes-goods-api';
import { Params } from '@lemoncloud/lemon-web-core';
import { useMutation, useQuery } from '@tanstack/react-query';

import { createQueryKeys } from '@codes/base-api';

import {
    createCatalog,
    deleteCatalog,
    fetchCatalogById,
    fetchCatalogSettingsById,
    fetchCatalogs,
    updateCatalog,
} from '../apis';
import {
    CREATE_CATALOG_KEY,
    DELETE_CATALOG_KEY,
    FETCH_CATALOG_BY_ID_KEY,
    FETCH_CATALOG_LIST_KEY,
    FETCH_CATALOG_LIST_WITH_NAME_KEY,
    UPDATE_CATALOG_KEY,
} from '../consts';
import {
    CreateCatalogBody,
    HandleErrorEventFunc,
    HandleSuccessEventFunc,
    PaginationType,
    UpdateCatalogBody,
} from '../types';

export const catalogsSettingsKey = createQueryKeys('product-settings');

export const useCreateCatalog = (
    handleSuccessEvent?: HandleSuccessEventFunc<CatalogView>,
    handleErrorEvent?: HandleErrorEventFunc
) =>
    useMutation({
        mutationKey: [CREATE_CATALOG_KEY],
        mutationFn: async (body: CreateCatalogBody) => await createCatalog(body),
        onSuccess(data) {
            handleSuccessEvent && handleSuccessEvent(data);
        },
        onError(error) {
            handleErrorEvent && handleErrorEvent(error);
        },
    });

export const useUpdateCatalog = (
    handleSuccessEvent?: HandleSuccessEventFunc<CatalogView>,
    handleErrorEvent?: HandleErrorEventFunc
) =>
    useMutation({
        mutationKey: [UPDATE_CATALOG_KEY],
        mutationFn: async (body: UpdateCatalogBody) => await updateCatalog(body),
        onSuccess(data) {
            handleSuccessEvent && handleSuccessEvent(data);
        },
        onError(error) {
            handleErrorEvent && handleErrorEvent(error);
        },
    });

export const useFetchCatalogs = (params: Params) =>
    useQuery<PaginationType<CatalogView[]>>({
        queryKey: [FETCH_CATALOG_LIST_KEY, { ...params }],
        queryFn: async () => {
            const result = await fetchCatalogs({ ...params });
            return { ...result, data: result.list || [] } as PaginationType<CatalogView[]>;
        },
        refetchOnWindowFocus: false,
        staleTime: 0,
    });

export const useFetchCatalogsWithName = (params: Params) =>
    useQuery<PaginationType<CatalogView[]>>({
        queryKey: [FETCH_CATALOG_LIST_WITH_NAME_KEY, { ...params }],
        queryFn: async () => {
            const result = await fetchCatalogs(params);
            return { ...result, data: result.list || [] } as PaginationType<CatalogView[]>;
        },
        refetchOnWindowFocus: false,
        enabled: !!params['name'],
    });

export const useFetchCatalogById = (catalogId?: string) => {
    return useQuery<CatalogView>({
        queryKey: [FETCH_CATALOG_BY_ID_KEY, catalogId],
        queryFn: () => fetchCatalogById(catalogId),
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        enabled: !!catalogId,
        staleTime: 0,
    });
};

export const useDeleteCatalog = (
    handleSuccessEvent?: HandleSuccessEventFunc<CatalogView>,
    handleErrorEvent?: HandleErrorEventFunc
) =>
    useMutation({
        mutationKey: [DELETE_CATALOG_KEY],
        mutationFn: async (catalogId: string) => await deleteCatalog(catalogId),
        onSuccess(data) {
            handleSuccessEvent && handleSuccessEvent(data);
        },
        onError(error) {
            handleErrorEvent && handleErrorEvent(error);
        },
    });

export const useCatalogSettingsById = (catalogId?: string) =>
    useQuery<SettingsView>({
        queryKey: catalogsSettingsKey.detail(catalogId as string),
        queryFn: () => fetchCatalogSettingsById(catalogId),
        refetchOnWindowFocus: false,
        enabled: !!catalogId,
        refetchOnMount: true,
        staleTime: 0,
    });
